import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useEffect } from 'react';
import { getQueryFn } from '@/lib/queryClient';
import { useAuth0 } from '@auth0/auth0-react';

interface AdminRouteGuardProps {
  children: React.ReactNode;
  requireSuperAdmin?: boolean;
}

export default function AdminRouteGuard({ children, requireSuperAdmin = false }: AdminRouteGuardProps) {
  const [location] = useLocation();
  const { loginWithRedirect, isLoading: isAuth0Loading, isAuthenticated } = useAuth0();
  
  const { data: authResponse, isLoading, error } = useQuery({
    queryKey: ['/api/auth/me'],
    queryFn: getQueryFn({ on401: "returnNull" }),
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const user = authResponse?.user;

  // Handle redirect in useEffect to avoid setState during render
  useEffect(() => {
    if (isLoading || isAuth0Loading) return;
    if (!user && !isAuthenticated) {
      try { sessionStorage.setItem('postLoginRedirect', location); } catch {}
      loginWithRedirect();
    }
  }, [isLoading, isAuth0Loading, user, isAuthenticated, loginWithRedirect, location]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-forestBlack flex items-center justify-center">
        <div className="text-primary text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Only SuperAdmin can access admin routes
  const hasAccess = (user.role === "SuperAdmin" || user.role === "superadmin" || user.role === "super_admin");

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-forestBlack flex items-center justify-center">
        <div className="text-center text-white p-6 bg-gray-800 rounded-lg max-w-md">
          <h1 className="text-2xl text-red-400 font-bold mb-4">Access Denied</h1>
          <p className="text-gray-300 mb-6">
            {requireSuperAdmin 
              ? "Only Super Administrators can access this page."
              : "You do not have permission to view this page."
            }
          </p>
          <button
            onClick={() => setLocation('/')}
            className="bg-primary text-black px-6 py-2 rounded font-semibold hover:bg-cyan-300"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
